import type { OrgSubscriptionView } from "@keydials/api-client";

/**
 * What the Subscription card says about an organisation's plan (KEY-635), worked out here so the words
 * can be tested without a browser. Nothing commercial is decided in this file: status, dates, price and
 * whether a card is on file all come from the server; this only chooses how to say them and which two
 * buttons apply.
 */

export interface SubscriptionDate {
  label: string;
  iso: string;
}

export interface SubscriptionSummary {
  /** The plan as the customer knows it — the public name, or family and tier when there is none. */
  name: string;
  /** "£5.99 a month", or "Granted" for a comp. */
  price: string;
  status: string;
  detail: string;
  dates: SubscriptionDate[];
  /**
   * A card-free trial, running or expired: the checkout is the only way to add a card, and it keeps the
   * trial's end date (KEY-633) rather than starting another one.
   */
  canSubscribeNow: boolean;
  /** Stripe's portal — change the card, read invoices, cancel. Only where Stripe is billing this. */
  canManageBilling: boolean;
}

export function familyLabel(family: string): string {
  switch (family) {
    case "PartnerPersonal":
      return "Partner · Personal";
    case "PartnerCommercial":
      return "Partner · Commercial";
    case "EducationPupils":
      return "Education · Pupils";
    case "EducationTrusts":
      return "Education · Trusts";
    default:
      return family;
  }
}

export function priceLabel(subscription: OrgSubscriptionView): string {
  if (subscription.collectionMethod === "Comp") return "Granted — nothing to pay";
  const amount = (subscription.amountPence / 100).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: subscription.amountPence % 100 === 0 ? 0 : 2,
  });
  return `${amount} ${subscription.period === "Annual" ? "a year" : "a month"}`;
}

export function summariseSubscription(s: OrgSubscriptionView): SubscriptionSummary {
  const name = s.displayName?.trim() || `${familyLabel(s.family)} ${s.tierName}`;
  if (s.sponsoredBy && s.collectionMethod === "Comp") return sponsoredSummary(s, name, s.sponsoredBy);

  const price = priceLabel(s);
  const stripe = s.isStripeBilled && s.collectionMethod !== "Comp";

  switch (s.status) {
    case "Trialing":
      if (s.cardFreeTrial) {
        return {
          name,
          price,
          status: "Free trial",
          // KEY-667: "nothing is deleted" is only unconditionally true while data retention is off for them.
          detail: s.dataRetentionOn
            ? "No card was taken. Add one before the trial ends and your plan carries straight on from that day; leave it and your dials pause until you subscribe, and are kept for a limited time after that."
            : "No card was taken. Add one before the trial ends and your plan carries straight on from that day; leave it and your dials pause until you subscribe. Nothing is deleted either way.",
          dates: s.trialEndsAtUtc ? [{ label: "Trial ends", iso: s.trialEndsAtUtc }] : [],
          canSubscribeNow: true,
          canManageBilling: false,
        };
      }
      return {
        name,
        price,
        status: "Free trial",
        detail: "Your card is on file and is first charged when the trial ends. Cancel before then and you pay nothing.",
        dates: s.trialEndsAtUtc ? [{ label: "Trial ends", iso: s.trialEndsAtUtc }] : [],
        canSubscribeNow: false,
        canManageBilling: stripe,
      };
    case "TrialExpired":
      return {
        name,
        price,
        status: "Trial ended",
        detail: "Your free trial has ended. Subscribe and everything is exactly where you left it.",
        dates: [],
        canSubscribeNow: true,
        canManageBilling: false,
      };
    case "Active":
      return {
        name,
        price,
        status: "Active",
        detail:
          s.collectionMethod === "Comp"
            ? "Granted by Key Dials; there is nothing to pay."
            : s.cancelAtPeriodEnd
              ? "Cancelled; your access continues until the period you have paid for ends."
              : "Billed automatically each period.",
        dates: s.currentPeriodEndUtc
          ? [{ label: s.cancelAtPeriodEnd ? "Access ends" : "Next payment", iso: s.currentPeriodEndUtc }]
          : [],
        canSubscribeNow: false,
        canManageBilling: stripe,
      };
    case "PastDue":
      return {
        name,
        price,
        status: "Payment overdue",
        detail: "Your last payment didn't go through. Update your card and the payment is retried automatically; your access is unchanged meanwhile.",
        dates: s.currentPeriodEndUtc ? [{ label: "Next payment", iso: s.currentPeriodEndUtc }] : [],
        canSubscribeNow: false,
        canManageBilling: stripe,
      };
    case "Canceled":
      return {
        name,
        price,
        status: "Cancelled",
        // KEY-667: "stay where they are" only while data retention is off for this organisation; once it is on,
        // an ended plan's data has an end date and the card says so.
        // KEY-911/912: an ended subscription is locked out, so the way back is offered here as it is for an
        // expired trial — checkout accepts Canceled, and the subscription it completes reuses this row.
        detail: !s.dataRetentionOn
          ? "Your subscription has ended. Your dials and data are safe. Subscribe again to pick up where you left off."
          : s.dataDeletionScheduledAtUtc
            ? "Your subscription has ended. Your dials are kept until the date below, then permanently deleted unless you subscribe again."
            : "Your subscription has ended. Your dials are kept for a limited time, and we'll email you the date before anything is deleted.",
        dates: [
          ...(s.currentPeriodEndUtc ? [{ label: "Access ended", iso: s.currentPeriodEndUtc }] : []),
          ...(s.dataRetentionOn && s.dataDeletionScheduledAtUtc
            ? [{ label: "Data deleted on", iso: s.dataDeletionScheduledAtUtc }]
            : []),
        ],
        canSubscribeNow: true,
        canManageBilling: stripe,
      };
    default:
      return { name, price, status: s.status, detail: "", dates: [], canSubscribeNow: false, canManageBilling: stripe };
  }
}

/**
 * A relative on somebody's family pack (KEY-651). Their plan is the buyer's rung, comped, so the ordinary
 * wording would quote a price they are not paying and call it "granted by Key Dials". Nothing to buy or
 * manage here either: the buyer pays, and it follows the buyer's plan.
 */
function sponsoredSummary(s: OrgSubscriptionView, name: string, payer: string): SubscriptionSummary {
  const base = { name, price: `Paid for by ${payer}`, dates: [], canSubscribeNow: false, canManageBilling: false };
  switch (s.status) {
    case "TrialExpired":
      return {
        ...base,
        status: "Paused",
        detail: `${payer}'s family plan has ended, so your dials are paused. Nothing has been deleted.`,
      };
    case "Canceled":
      return {
        ...base,
        status: "Ended",
        detail: `${payer}'s family plan has ended. Your dials stay where they are.`,
      };
    default:
      return {
        ...base,
        status: "Active",
        detail: `Part of ${payer}'s family plan. It's yours alone: they pay for it, but can't see anything in your dials.`,
      };
  }
}

export function formatSubscriptionDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
